import React from 'react';
import { Box, Stack, Typography, Button, Table, TableHead, TableRow, TableCell, TableBody, Chip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Menu, MenuItem } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import api from '../../services/api';
import { useNotification } from '../layouts/Layout';

interface EmailLog {
  id: number;
  created_at: string;
  to: string;
  subject: string;
  body_html: string;
  status: 'sent'|'failed';
}

const EmailLogsList: React.FC = () => {
  const { showNotification } = useNotification();
  const [items, setItems] = React.useState<EmailLog[]>([]);
  const [preview, setPreview] = React.useState<EmailLog | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const [selectedRow, setSelectedRow] = React.useState<EmailLog | null>(null);
  const [resending, setResending] = React.useState(false);

  const load = async () => {
    const res = await api.get('/notifications/email/logs');
    setItems(res.data?.data || []);
  };

  React.useEffect(() => { load(); }, []);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, row: EmailLog) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedRow(null);
  };

  const handleResend = async () => {
    if (!selectedRow) return;
    
    try {
      setResending(true);
      console.log('[EmailLogsList] Resending email with id:', selectedRow.id);
      const response = await api.post(`/notifications/email/logs/${selectedRow.id}/resend`);
      console.log('[EmailLogsList] Resend response:', response.data);
      showNotification('Email został wysłany ponownie', 'success');
      handleMenuClose();
      load(); // Odśwież listę
    } catch (error: any) {
      console.error('[EmailLogsList] Resend error:', error);
      const errorMessage = error.response?.data?.message || error.response?.statusText || error.message || 'Nieznany błąd';
      showNotification('Błąd wysyłania emaila: ' + errorMessage, 'error');
    } finally {
      setResending(false);
    }
  };

  // Format MySQL datetime string (UTC, no timezone) to local time
  const formatUtcMysqlToLocal = (value: string): string => {
    if (!value) return '';
    try {
      // Handle values like 'YYYY-MM-DD HH:mm:ss' (no timezone)
      const mysqlNoTzPattern = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;
      const normalized = mysqlNoTzPattern.test(value)
        ? value.replace(' ', 'T') + 'Z'
        : value;
      const date = new Date(normalized);
      if (isNaN(date.getTime())) return value; // Fallback to raw value if unparsable
      return date.toLocaleString();
    } catch {
      return value;
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle1">Lista powiadomień</Typography>
        <Button size="small" variant="outlined" href={`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/notifications/email/logs.csv`} target="_blank">Eksport CSV</Button>
      </Stack>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>Data wysłania</TableCell>
            <TableCell>Adresat</TableCell>
            <TableCell>Temat</TableCell>
            <TableCell>Treść</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Akcje</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map(row => (
            <TableRow key={row.id} hover>
              <TableCell>{row.id}</TableCell>
              <TableCell>{formatUtcMysqlToLocal(row.created_at)}</TableCell>
              <TableCell>{(row.to || '').split(',')[0]}</TableCell>
              <TableCell>{(row.subject || '').slice(0, 30)}</TableCell>
              <TableCell>{String(row.body_html || '').replace(/<[^>]+>/g,' ').slice(0,50)}</TableCell>
              <TableCell>{row.status === 'sent' ? <Chip size="small" label="wysłane" color="success"/> : <Chip size="small" label="nieudana próba" color="error"/>}</TableCell>
              <TableCell align="right">
                <IconButton 
                  size="small" 
                  onClick={(e) => handleMenuOpen(e, row)}
                >
                  <MoreVertIcon fontSize="small"/>
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={() => { setPreview(selectedRow); handleMenuClose(); }}>
          Podgląd
        </MenuItem>
        <MenuItem onClick={handleResend} disabled={resending}>
          {resending ? 'Wysyłanie...' : 'Wyślij ponownie'}
        </MenuItem>
      </Menu>

      <Dialog open={!!preview} onClose={() => setPreview(null)} maxWidth="md" fullWidth>
        <DialogTitle>Podgląd wiadomości #{preview?.id}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Do: {(preview?.to || '')}</Typography>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>Temat: {preview?.subject}</Typography>
          <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2 }} dangerouslySetInnerHTML={{ __html: preview?.body_html || '' }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreview(null)}>Zamknij</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmailLogsList;


